import { NcaLayerError } from './ncalayer-error';

/**
 * NCALayer — the desktop application a signer runs, reached over a local WebSocket.
 *
 * The same client WebAdmin uses, and for the same reasons each detail is there: builds differ in
 * whether they bind `localhost` or `127.0.0.1` and whether they present a certificate, so four
 * addresses are tried in order; a refused connection can hang, so each attempt has two seconds;
 * signing waits for a person to type a key password, so an operation has sixty; and NCALayer sends an
 * unsolicited version frame before the answer, which is skipped rather than taken as an empty
 * signature. The socket is closed after every call.
 *
 * NCALayer is desktop software. A customer on a phone cannot sign this way, which is why the portal
 * keeps the drawn signature beside it.
 */
const SOCKET_URLS = ['wss://127.0.0.1:13579', 'ws://127.0.0.1:13579', 'wss://localhost:13579', 'ws://localhost:13579'];

const CONNECT_TIMEOUT_MS = 2000;
const OPERATION_TIMEOUT_MS = 60000;

const MODULE = 'kz.gov.pki.knca.commonUtils';

/** What NCALayer sends back, including the version frame that is not an answer. */
type NcaLayerFrame = {
    code?: string;
    message?: string;
    responseObject?: string;
    result?: { version?: string };
};

/**
 * Signs the server's package hash and returns the CMS.
 *
 * The trailing `true` asks for attached data, and it is load-bearing: the signed value is embedded in
 * the CMS, which is how the server proves the signature covers this package.
 *
 * @param packageHashBase64 The value the server issued. It is never computed here.
 */
export async function signPackageHash(packageHashBase64: string, storage = 'PKCS12'): Promise<string> {
    const socket = await connect();

    try {
        const cms = await send(socket, {
            module: MODULE,
            method: 'createCMSSignatureFromBase64',
            args: [storage, 'SIGNATURE', packageHashBase64, true]
        });

        if (cms === '') {
            throw new NcaLayerError('empty');
        }

        return cms;
    } finally {
        socket.close();
    }
}

function connect(index = 0): Promise<WebSocket> {
    if (index >= SOCKET_URLS.length) {
        return Promise.reject(new NcaLayerError('unavailable'));
    }

    return new Promise<WebSocket>((resolve, reject) => {
        let settled = false;

        const next = () => {
            if (settled) {
                return;
            }

            settled = true;
            connect(index + 1).then(resolve, reject);
        };

        let socket: WebSocket;

        try {
            socket = new WebSocket(SOCKET_URLS[index]);
        } catch {
            // A blocked mixed-content or malformed URL throws synchronously in some browsers.
            next();
            return;
        }

        const timer = setTimeout(() => {
            socket.close();
            next();
        }, CONNECT_TIMEOUT_MS);

        socket.onopen = () => {
            clearTimeout(timer);
            settled = true;
            resolve(socket);
        };

        socket.onerror = () => {
            clearTimeout(timer);
            next();
        };
    });
}

function send(socket: WebSocket, command: unknown): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        let settled = false;

        const finish = (action: () => void) => {
            if (settled) {
                return;
            }

            settled = true;
            clearTimeout(timer);
            socket.removeEventListener('message', onMessage);
            action();
        };

        const onMessage = (event: MessageEvent<string>) => {
            let payload: NcaLayerFrame;

            try {
                payload = JSON.parse(event.data) as NcaLayerFrame;
            } catch {
                finish(() => reject(new NcaLayerError('unreadable')));
                return;
            }

            // The unsolicited version announcement. Not an answer, so keep listening.
            if (payload.result?.version !== undefined) {
                return;
            }

            if (payload.code === '200') {
                finish(() => resolve(payload.responseObject ?? ''));
                return;
            }

            if (payload.code !== undefined) {
                finish(() => reject(new NcaLayerError('refused', payload.message)));
            }
        };

        const timer = setTimeout(() => finish(() => reject(new NcaLayerError('timeout'))), OPERATION_TIMEOUT_MS);

        socket.addEventListener('message', onMessage);
        socket.send(JSON.stringify(command));
    });
}
